/* eslint-disable max-lines */
import {
    APIResponse,
    CancellationReasonGroup,
    CancellationRequestSummary,
    Notice,
    PortfolioProperty,
    PropertyAppliance,
    PropertyContact,
    PropertyKeyPickupLocation,
    UpdatePropertyDetails,
    Document,
    IntegratedProperty,
    IntegratedPropertyUpdate,
    EntryMethodPreferenceSettings,
    EntryMethodPreferencesPayload,
    BasicPropertyData,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';
import { getBFFApiClient } from '@acp/feature/auth';

async function getProperty(
    propertyId: number | string
) {
    const response = await getBFFApiClient()
        .get<APIResponse<PortfolioProperty>>(
        `/v1/property/${propertyId}`
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property');
    }

    return response.data.data;
}

/**
 * Allows limited information about any property in the system.
 *
 * Will not expose any sensitive information.
 * Only really exposes the address.
 *
 * Necessary for minimum standards as we may not have access to the property.
 */
async function getBasicProperty(
    propertyId: number | string
) {
    const response = await getBFFApiClient()
        .get<APIResponse<BasicPropertyData>>(
        `/v1/property/${propertyId}`,
        {
            params: {
                basic: '1',
            },
        }
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property');
    }

    return response.data.data;
}

async function editPropertyDetails(
    propertyId: number,
    data: UpdatePropertyDetails
) {
    const response = await getBFFApiClient().put<APIResponse<null>>(`/v1/property/${propertyId}`, data);

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to edit property');
    }

    return response.data.data;
}

async function createHoldingServiceRequest(
    propertyId: number,
    services: number[],
    holdDate: number,
    reasonNote: string
) {
    const response = await getBFFApiClient().post<APIResponse<string>>(
        `/v1/property/${propertyId}/hold-request`,
        {
            services,
            holdDate,
            reasonNote,
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to send request: ${response.data.error}`);
    }

    return response.data.data;
}

async function getCancellationReasons() {
    const response = await getBFFApiClient().get<APIResponse<CancellationReasonGroup[]>>(
        '/v1/meta/cancellation-reasons'
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get cancellation reasons');
    }

    return response.data.data;
}

async function getCancellationRequestSummary(propertyId: number | string) {
    const response = await getBFFApiClient().get<APIResponse<CancellationRequestSummary>>(
        `/v1/property/${propertyId}/cancellation-request`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get cancellation request summary');
    }

    return response.data.data;
}

async function createCancellationRequest(
    propertyId: number | string,
    services: number[],
    cancellationReasonId: number,
    notes: string | null
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(
        `/v1/property/${propertyId}/cancellation-request`,
        {
            services,
            cancellationReasonId,
            notes,
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to create cancellation request');
    }

    return response.data.data;
}

async function getPropertyContactList(propertyId: number, {
    includeLandlords = false,
    includeOccupants = false,
    includePropertyManagers = false,
}: {
    includeLandlords?: boolean;
    includePropertyManagers?: boolean;
    includeOccupants?: boolean;
}) {
    const queryParams: Record<string, unknown> = {};

    if (includeLandlords) {
        queryParams.landlords = 1;
    }

    if (includeOccupants) {
        queryParams.occupants = 1;
    }

    if (includePropertyManagers) {
        queryParams.propertyManagers = 1;
    }

    const response = await getBFFApiClient().get<APIResponse<PropertyContact[]>>(`/v1/property/${propertyId}/contacts`, {
        params: queryParams,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property contacts');
    }

    return response.data.data;
}

async function getPropertyAppliances(propertyId: string | number, service: string | null) {
    const response = await getBFFApiClient().get<APIResponse<PropertyAppliance[]>>(
        `/v1/property/${propertyId}/appliances`,
        {
            params: {
                service,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property appliances');
    }

    return response.data.data;
}

async function getPropertyActiveNotices(propertyId: number, resolved: boolean) {
    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: Notice[];
    }>>(`/v1/property/${propertyId}/notices`, {
        params: {
            resolved,
        },
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property notices: ${response.data.error}`);
    }

    return response.data.data;
}

async function getPropertyKeyPickupLocation(propertyId: number | string) {
    const response = await getBFFApiClient().get<APIResponse<PropertyKeyPickupLocation>>(
        `/v1/property/${propertyId}/key-pickup-location`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property key pickup location');
    }

    return response.data.data;
}

async function getPropertyDocuments(
    propertyId: number | string,
    filters: string[],
    facets: string[]
) {
    const response = await getBFFApiClient().get<APIResponse<{
        totalCount: number;
        resultsCount: number;
        items: Document[];
    }>>(`/v1/property/${propertyId}/documents`, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: {
            filters,
            facets,
        },
    });

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property documents not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property documents');
    }

    return response.data.data;
}

async function getIntegratedProperty(agencyPortfolioPropertyId: number | string) {
    const response = await getBFFApiClient().get<APIResponse<IntegratedProperty>>(
        `/v1/integrated-property/${agencyPortfolioPropertyId}`
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Integrated property not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get integrated property');
    }

    return response.data.data;
}

async function updateIntegratedProperty(
    agencyPortfolioPropertyId: number,
    payload: IntegratedPropertyUpdate
) {
    const response = await getBFFApiClient().patch<APIResponse<null>>(
        `/v1/integrated-property/${agencyPortfolioPropertyId}`,
        payload
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update integrated property');
    }

    return response.data.data;
}

async function getPropertyEntryMethodPreference(propertyId: number | string) {
    const response = await getBFFApiClient().get<APIResponse<EntryMethodPreferenceSettings>>(
        `/v1/property/${propertyId}/entry-method-preferences`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property entry method preferences');
    }

    return response.data.data;
}

async function updatePropertyEntryMethodPreference(
    propertyId: number | string,
    preferencesPayload: EntryMethodPreferencesPayload
) {
    const response = await getBFFApiClient().post<APIResponse<{ success: boolean }>>(
        `/v1/property/${propertyId}/entry-method-preferences`,
        preferencesPayload
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update entry method preference');
    }

    return response.data.data;
}

async function markPropertyAsVacant(propertyId: number) {
    const response = await getBFFApiClient().post<APIResponse<{ success: boolean }>>(
        `/v1/property/${propertyId}/mark-as-vacant`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(response.data?.error ?? 'Failed to mark this property as vacant');
    }

    return response.data.data;
}

export {
    getProperty,
    editPropertyDetails,
    getPropertyDocuments,
    getCancellationReasons,
    getCancellationRequestSummary,
    createHoldingServiceRequest,
    createCancellationRequest,
    getPropertyContactList,
    getPropertyAppliances,
    getPropertyActiveNotices,
    getPropertyKeyPickupLocation,
    getIntegratedProperty,
    updateIntegratedProperty,
    getPropertyEntryMethodPreference,
    updatePropertyEntryMethodPreference,
    markPropertyAsVacant,
    getBasicProperty,
};
